import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { axiosInstanceForTenantAPI } from '../../../../../assets/utils/axiosInstance';
import getErrorMessage from '../../../../../assets/utils/getErrorMessage';
import { useAuth } from '../../../../../contexts/auth/authContext';
import { useToastNotificationContext } from '../../../../../hooks/global/alert/useToastNotification';
import ErrorResponseObject from '../../../../../interfaces/errorResponseObject';
import { setReport } from '../../../../../slices/reportDataSlice';
import { RootState } from '../../../../../utils/store';
import SwitchButtonToggle from '../../toggle/toolbar/swtichButtontoggle';

interface CustomerToggleLockReportProps {
  disabledFlag: boolean | undefined;
}


const CaseIdentifier = ({
  disabledFlag,
}: CustomerToggleLockReportProps) => {
  const dispatch = useDispatch();
  const { slug } = useAuth();
  const [companySlug, setCompanySlug] = useState<string | null>(slug);

  const { createToastNotification } = useToastNotificationContext();
  const intl = useIntl();
  const report = useSelector((state: RootState) => state.reportData.report);
  useEffect(() => {
    if (!slug) {
      setCompanySlug(localStorage.getItem('slug'));
    }
  }, [slug]);
  const { isCaseDoNotPurgeEnabled, isCasePrivilegedEnabled, referenceId } = report;
  const [isDoNotPurgeEnabled, setIsDoNotPurgeEnabled] = useState<boolean>(isCaseDoNotPurgeEnabled);
  const [isPrivilegedEnabled, setIsPrivilegedEnabled] = useState<boolean>(isCasePrivilegedEnabled);
  const [isRequestLoading, setIsRequestLoading] = useState<boolean>(false);

  // handle onchange for toggle
  const handleToggleAutoResponseDoNotPurge = async (newState: boolean) => {
    setIsRequestLoading(true);
    setIsDoNotPurgeEnabled(newState);
    try {
      const body = {
        is_case_do_not_purge_enabled: newState,
      };

      await axiosInstanceForTenantAPI(companySlug).patch(
        `/report/update/${referenceId}`,
        body,
      );
      let message = '';
      if (newState === true) {
        message = intl.formatMessage({ defaultMessage: 'Case Identifier: Do not Purge Enabled.' });
      } else {
        message = intl.formatMessage({ defaultMessage: 'Case Identifier: Do not Purge Disabled.' });
      }
      createToastNotification(message, 'success');
      dispatch(setReport({ ...report, isCaseDoNotPurgeEnabled: newState }));
      setIsRequestLoading(false);
      return;
    } catch (err) {
      const customError = getErrorMessage(err as ErrorResponseObject);
      createToastNotification(customError, 'error');
      dispatch(setReport({ ...report, isCaseDoNotPurgeEnabled: !newState }));
      setIsDoNotPurgeEnabled(!newState);
      setIsRequestLoading(false);
    }
  };
  const handleToggleAutoResponsePrivileged = async (newState: boolean) => {
    setIsRequestLoading(true);
    setIsPrivilegedEnabled(newState);
    try {
      const body = {
        is_case_privileged_enabled: newState,
      };

      await axiosInstanceForTenantAPI(companySlug).patch(
        `/report/update/${referenceId}`,
        body,
      );
      let message = '';
      if (newState === true) {
        message = intl.formatMessage({ defaultMessage: 'Case Identifier: Privileged Enabled.' });
      } else {
        message = intl.formatMessage({ defaultMessage: 'Case Identifier: Privileged Disabled.' });
      }
      createToastNotification(message, 'success');
      dispatch(setReport({ ...report, isCasePrivilegedEnabled: newState }));
      setIsRequestLoading(false);
      return;
    } catch (err) {
      const customError = getErrorMessage(err as ErrorResponseObject);
      createToastNotification(customError, 'error');
      dispatch(setReport({ ...report, isCasePrivilegedEnabled: !newState }));
      setIsPrivilegedEnabled(!newState);
      setIsRequestLoading(false);
    }
  };

  return (
    <Stack
      sx={{
        padding: '0px 10px',
        margin: '18px 0px 18px 0px',
      }}
    >
      <Typography>
        <FormattedMessage defaultMessage='Case Identifier' />
      </Typography>
      <Stack
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          m: '2px 0px 2px 0px',
        }}
        gap={2}
      >
        <Stack direction='row' >
          <SwitchButtonToggle
            disabled={isRequestLoading || disabledFlag ? true : false}
            toggleSwitchState={isDoNotPurgeEnabled}
            onToggle={() => handleToggleAutoResponseDoNotPurge(!isCaseDoNotPurgeEnabled)}
          />

          <Typography
            sx={{
              color: 'var(--grey-white-background, #878787)',
              fontFamily: 'Schibsted Grotesk',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '24px',
              marginRight: '2px',
              textAlign: 'left',
              width: '100%',
            }}
          >
            <FormattedMessage defaultMessage='Do Not Purge' />
          </Typography>
        </Stack>
        <Stack direction='row'>
          <SwitchButtonToggle
            disabled={isRequestLoading || disabledFlag ? true : false}
            toggleSwitchState={isPrivilegedEnabled}
            onToggle={() => handleToggleAutoResponsePrivileged(!isCasePrivilegedEnabled)}
          />
          <Typography
            sx={{
              color: 'var(--grey-white-background, #878787)',
              fontFamily: 'Schibsted Grotesk',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '24px',
              marginRight: '2px',
              textAlign: 'left',
              width: '100%',
            }}
          >
            <FormattedMessage defaultMessage='Privileged' />
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default CaseIdentifier;
